import { useEffect, useRef } from "react";

const TikTokEmbed = ({ embedHtml }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Load the TikTok embed script dynamically for this specific embed
    if (containerRef.current) {
      const script = document.createElement("script");
      script.src = "https://www.tiktok.com/embed.js";
      script.async = true;
      containerRef.current.appendChild(script);

      return () => {
        // Cleanup: remove script when component unmounts
        if (containerRef.current && script.parentNode) {
          containerRef.current.removeChild(script);
        }
      };
    }
  }, [embedHtml]);

  // Parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(embedHtml, "text/html");
  const blockquote = doc.querySelector("blockquote");

  if (!blockquote) {
    return <div>Invalid embed code</div>;
  }

  // Extract blockquote attributes
  const cite = blockquote.getAttribute("cite");
  const videoId = blockquote.getAttribute("data-video-id");
  const embedFrom = blockquote.getAttribute("data-embed-from");
  const styleAttr = blockquote.getAttribute("style");

  // Parse style string to JSX style object
  const styleObj = {};
  if (styleAttr) {
    styleAttr.split(";").forEach((rule) => {
      const [property, value] = rule.split(":").map((s) => s?.trim());
      if (property && value) {
        // Convert kebab-case to camelCase (max-width -> maxWidth)
        const camelProperty = property.replace(/-([a-z])/g, (g) =>
          g[1].toUpperCase(),
        );
        styleObj[camelProperty] = value;
      }
    });
  }

  // Parse the section content
  const section = blockquote.querySelector("section");

  if (!section) {
    return <div>Invalid embed structure</div>;
  }

  // Convert section HTML to React elements
  const parseSectionContent = (sectionElement) => {
    const elements = [];
    let keyCounter = 0;

    const parseNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const children = Array.from(node.childNodes).map((child) =>
          parseNode(child),
        );

        const key = keyCounter++;
        const props = {};

        // Copy attributes (but NOT key)
        if (node.hasAttribute("href")) props.href = node.getAttribute("href");
        if (node.hasAttribute("target"))
          props.target = node.getAttribute("target");
        if (node.hasAttribute("title"))
          props.title = node.getAttribute("title");
        if (node.hasAttribute("rel")) props.rel = node.getAttribute("rel");

        switch (tagName) {
          case "a":
            return (
              <a key={key} {...props}>
                {children}
              </a>
            );
          case "p":
            return <p key={key}>{children}</p>;
          default:
            return children;
        }
      }

      return null;
    };

    Array.from(sectionElement.childNodes).forEach((child) => {
      elements.push(parseNode(child));
    });

    return elements;
  };

  const sectionContent = parseSectionContent(section);

  return (
    <div ref={containerRef}>
      <blockquote
        className="tiktok-embed"
        cite={cite}
        data-video-id={videoId}
        data-embed-from={embedFrom}
        style={styleObj}
      >
        <section>{sectionContent}</section>
      </blockquote>
    </div>
  );
};

export default TikTokEmbed;
